import React from 'react';
import {
  Truck,
  Clock,
  Globe,
  Package,
  CheckCircle,
  AlertCircle,
  MapPin,
} from 'lucide-react';
import DynamicPageSections from '../components/site/DynamicPageSections';
import { useSiteStructureSections } from '../hooks/useSiteStructureSections';

const ShippingHeroStatic: React.FC = () => (
  <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-16 md:py-20">
    <div className="container mx-auto px-4">
      <div className="max-w-3xl mx-auto text-center">
        <Truck className="w-16 h-16 mx-auto mb-4" />
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Shipping Information</h1>
        <p className="text-xl text-blue-100">
          Fast, reliable shipping to your doorstep. Free delivery on orders over ₹4,999.
        </p>
      </div>
    </div>
  </section>
);

const ShippingRest: React.FC = () => (
  <>
    <section className="py-12 -mt-8">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Package className="w-7 h-7 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Standard Shipping</h3>
            <p className="text-gray-600 mb-3">5-7 business days</p>
            <p className="text-2xl font-bold text-blue-600">₹499</p>
            <p className="text-sm text-gray-500 mt-2">Free on orders over ₹4,999</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow border-2 border-blue-500 relative">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">Popular</span>
            </div>
            <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4 mt-2">
              <Clock className="w-7 h-7 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Express Shipping</h3>
            <p className="text-gray-600 mb-3">2-3 business days</p>
            <p className="text-2xl font-bold text-green-600">₹1,099</p>
            <p className="text-sm text-gray-500 mt-2">Free on orders over ₹12,499</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Globe className="w-7 h-7 text-purple-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">International</h3>
            <p className="text-gray-600 mb-3">7-14 business days</p>
            <p className="text-2xl font-bold text-purple-600">₹1,699</p>
            <p className="text-sm text-gray-500 mt-2">Calculated at checkout</p>
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Delivery Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Order Processing</h3>
                  <p className="text-gray-600 text-sm">
                    Orders are processed within 24 hours (excluding weekends and holidays)
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Delivery Areas</h3>
                  <p className="text-gray-600 text-sm">We ship to all 50 US states and 100+ countries worldwide</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Packaging</h3>
                  <p className="text-gray-600 text-sm">All items are carefully packed to ensure safe delivery</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Tracking</h3>
                  <p className="text-gray-600 text-sm">Track your order in real-time from dispatch to delivery</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Signature Required</h3>
                  <p className="text-gray-600 text-sm">Orders over ₹16,499 require signature upon delivery</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Delivery Times</h3>
                  <p className="text-gray-600 text-sm">Deliveries are made Monday through Saturday, 9 AM - 8 PM</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Shipping Rates</h2>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold text-gray-900">Destination</th>
                    <th className="px-6 py-4 text-center font-semibold text-gray-900">Standard</th>
                    <th className="px-6 py-4 text-center font-semibold text-gray-900">Express</th>
                    <th className="px-6 py-4 text-center font-semibold text-gray-900">Delivery Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="px-6 py-4 text-gray-900">United States</td>
                    <td className="px-6 py-4 text-center text-gray-600">
                      ₹499 <span className="text-green-600 text-sm">(Free over ₹4,999)</span>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-600">
                      ₹1,099 <span className="text-green-600 text-sm">(Free over ₹12,499)</span>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-600">2-7 days</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-900">Canada</td>
                    <td className="px-6 py-4 text-center text-gray-600">₹899</td>
                    <td className="px-6 py-4 text-center text-gray-600">₹1,699</td>
                    <td className="px-6 py-4 text-center text-gray-600">5-10 days</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-900">Europe</td>
                    <td className="px-6 py-4 text-center text-gray-600">₹1,299</td>
                    <td className="px-6 py-4 text-center text-gray-600">₹3,499</td>
                    <td className="px-6 py-4 text-center text-gray-600">7-14 days</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-900">Asia Pacific</td>
                    <td className="px-6 py-4 text-center text-gray-600">₹2,499</td>
                    <td className="px-6 py-4 text-center text-gray-600">₹4,199</td>
                    <td className="px-6 py-4 text-center text-gray-600">10-20 days</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-900">Rest of World</td>
                    <td className="px-6 py-4 text-center text-gray-600">₹1,999</td>
                    <td className="px-6 py-4 text-center text-gray-600">₹4,199</td>
                    <td className="px-6 py-4 text-center text-gray-600">14-30 days</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Shipping FAQ</h2>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-2">When will my order ship?</h3>
              <p className="text-gray-600">
                Orders are processed within 24 hours. You&apos;ll receive a shipping confirmation email with tracking
                information once your order is dispatched.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Can I change my shipping address?</h3>
              <p className="text-gray-600">
                Yes, you can change your shipping address within 2 hours of placing your order. Contact our customer
                service for assistance.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Do you ship to PO boxes?</h3>
              <p className="text-gray-600">
                Yes, we ship to PO boxes via USPS for standard shipping. Express shipping requires a physical address.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-2">What if my package is lost?</h3>
              <p className="text-gray-600">
                If your package hasn&apos;t arrived within 30 days of shipping, please contact us. We&apos;ll investigate
                and provide a replacement or refund.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
);

const ShippingInfo: React.FC = () => {
  const { sections, ready } = useSiteStructureSections('/shipping');
  const showCmsHeader = ready && sections.length > 0;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 pt-16">
      <main className="flex-grow">
        {showCmsHeader ? <DynamicPageSections sections={sections} /> : <ShippingHeroStatic />}
        <ShippingRest />
      </main>
    </div>
  );
};

export default ShippingInfo;
